import { createStore, type StoreApi } from 'zustand';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '@/util/platformStorage';

/**
 * UI state for one visit to Research: everything the screens need that
 * is not the conversation itself.
 *
 * Turns, results and titles never live here. They are server state and belong
 * to the conversation snapshot in the Query cache, which realtime hints and
 * focus refetch keep current; a second copy here would disagree with it during
 * every refetch. Nothing in this store is clinical text except `handoffDraft`,
 * and that is held only long enough for the next conversation to read it once.
 *
 * One store per mount of the Ask layout, which outlives the move from a new
 * conversation to its saved address, so the draft, the socket state and the
 * record of what this device submitted survive that navigation. It is never a
 * module singleton, so a sign-out, which unmounts the layout, discards it.
 *
 * The desktop history column's collapsed state has two writers. The reader's
 * toggle is a preference: it must hold when they start or open another
 * conversation, including one begun from the Inquiry Library, which mounts a
 * fresh layout. So the toggle also writes it to web storage, read back
 * synchronously so the column never flashes open. A new topic's first question
 * landing folds the column too, but that is the app's choice, not the
 * reader's, so it lasts for the visit and is never written down, and it cannot
 * overwrite a reader who chose to keep the column open. Only a layout
 * preference is stored, not clinical text. Native has no web storage and keeps
 * either for the visit only.
 */

export type AskConnectionState = 'connecting' | 'live' | 'down' | 'refused';

/** Why the reader was moved to a new conversation, said once on arrival. */
export type AskNotice = 'not_found' | 'deleted_elsewhere' | 'conversation_full';

/**
 * How the next saved conversation was reached, for the opened event. `continued`
 * is a new conversation arriving at its saved address, already reported as new.
 */
export type AskEntry = 'history' | 'continued';

export type AskSelection = {
  conversationId: string;
  turnId: string;
  source: string;
  resultId: string;
};

type AskUiState = {
  selected: AskSelection | null;
  /** A draft carried into a new conversation after the previous one filled up. */
  handoffDraft: string | null;
  /** Turns this device submitted, by id, with when they were sent. */
  submittedTurns: ReadonlyMap<string, number>;
  connection: AskConnectionState;
  /** The open conversation was deleted on another device. */
  deletedElsewhere: string | null;
  /**
   * Conversations this device is deleting. Their own `conversation.deleted`
   * hint can land before the delete's response does, and must not be read as
   * a delete from somewhere else.
   */
  deletingHere: ReadonlySet<string>;
  notice: AskNotice | null;
  nextEntry: AskEntry | null;
  isHistoryCollapsed: boolean;
};

type AskUiActions = {
  selectResult: (selection: AskSelection | null) => void;
  setHandoffDraft: (draft: string | null) => void;
  takeHandoffDraft: () => string | null;
  markSubmitted: (turnId: string, submittedAt: number) => void;
  forgetSubmitted: (turnId: string) => void;
  setConnection: (connection: AskConnectionState) => void;
  markDeletedElsewhere: (conversationId: string | null) => void;
  setDeletingHere: (conversationId: string, isDeleting: boolean) => void;
  setNotice: (notice: AskNotice | null) => void;
  setNextEntry: (entry: AskEntry | null) => void;
  takeNextEntry: () => AskEntry | null;
  setHistoryCollapsed: (isCollapsed: boolean) => void;
  foldHistoryForVisit: () => void;
};

export type AskUiStore = AskUiState & AskUiActions;

const INITIAL_STATE: AskUiState = {
  selected: null,
  handoffDraft: null,
  submittedTurns: new Map(),
  connection: 'connecting',
  deletedElsewhere: null,
  deletingHere: new Set(),
  notice: null,
  nextEntry: null,
  isHistoryCollapsed: false,
};

export function createAskUiStore(): StoreApi<AskUiStore> {
  return createStore<AskUiStore>((set, get) => ({
    ...INITIAL_STATE,
    isHistoryCollapsed: safeLocalStorageGetItem(STORAGE_KEYS.RESEARCH_HISTORY_COLLAPSED) === 'true',

    selectResult: (selection) => set({ selected: selection }),

    setHandoffDraft: (draft) => set({ handoffDraft: draft }),

    takeHandoffDraft: () => {
      const draft = get().handoffDraft;

      if (draft != null) {
        set({ handoffDraft: null });
      }

      return draft;
    },

    markSubmitted: (turnId, submittedAt) =>
      set((state) => ({ submittedTurns: new Map(state.submittedTurns).set(turnId, submittedAt) })),

    forgetSubmitted: (turnId) =>
      set((state) => {
        if (!state.submittedTurns.has(turnId)) {
          return state;
        }

        const submittedTurns = new Map(state.submittedTurns);

        submittedTurns.delete(turnId);

        return { submittedTurns };
      }),

    setConnection: (connection) =>
      set((state) => (state.connection === connection ? state : { connection })),

    markDeletedElsewhere: (conversationId) => set({ deletedElsewhere: conversationId }),

    setDeletingHere: (conversationId, isDeleting) =>
      set((state) => {
        if (state.deletingHere.has(conversationId) === isDeleting) {
          return state;
        }

        const deletingHere = new Set(state.deletingHere);

        if (isDeleting) {
          deletingHere.add(conversationId);
        } else {
          deletingHere.delete(conversationId);
        }

        return { deletingHere };
      }),

    setNotice: (notice) => set({ notice }),

    setNextEntry: (entry) => set({ nextEntry: entry }),

    takeNextEntry: () => {
      const entry = get().nextEntry;

      if (entry != null) {
        set({ nextEntry: null });
      }

      return entry;
    },

    setHistoryCollapsed: (isCollapsed) => {
      safeLocalStorageSetItem(STORAGE_KEYS.RESEARCH_HISTORY_COLLAPSED, String(isCollapsed));

      set((state) =>
        state.isHistoryCollapsed === isCollapsed ? state : { isHistoryCollapsed: isCollapsed }
      );
    },

    foldHistoryForVisit: () =>
      set((state) => (state.isHistoryCollapsed ? state : { isHistoryCollapsed: true })),
  }));
}

/** Whether the socket can be trusted to announce changes, so polling can stop. */
export function selectIsLive(state: AskUiStore): boolean {
  return state.connection === 'live';
}

/**
 * The selected result for one turn, or null. Lets each turn re-render only
 * when its own selection changes.
 */
export function selectSelectedResultIdForTurn(
  turnId: string
): (state: AskUiStore) => string | null {
  return (state) => (state.selected?.turnId === turnId ? state.selected.resultId : null);
}
